'use client';

import {
  BREAKPOINTS_V3,
  GridItem,
  InverseHeader,
  LayoutGrid,
  SwipeableProgressRatio,
  SwipeableProvider,
  SwipeableSlider,
  TypographyV3,
} from '@jouzen/ecom-components';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { type PropsWithChildren, useState } from 'react';

import BackdropBlur from '@/app/components/BackdropBlur';
import Motion from '@/app/components/Motion';
import NavButtons from '@/app/components/SwipeableNavButtons';

interface CardProps extends Readonly<PropsWithChildren> {
  readonly tagKey: string;
  readonly mainImageSrc: string;
  readonly appUiImageSrc: string;
}

const DesktopCard = ({
  tagKey,
  mainImageSrc,
  appUiImageSrc,
}: Readonly<CardProps>) => {
  const t = useTranslations();
  const sizes = `(min-width: ${BREAKPOINTS_V3.large}px) 33vw, 50vw`;
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => void setIsHovered(true);
  const handleMouseLeave = () => void setIsHovered(false);

  return (
    <li>
      <div
        className="relative flex h-full flex-1 flex-col gap-y-4"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative h-full">
          <div className="absolute z-[99] ml-6 mt-4 inline-flex px-3 py-2">
            <BackdropBlur variant="pill" />
            <TypographyV3
              Element="span"
              color="light"
              variant="eyebrow-large"
              className="relative"
            >
              {t(tagKey)}
            </TypographyV3>
          </div>
          <Image
            className="rounded-xl object-cover"
            alt=""
            src={mainImageSrc}
            fill
            sizes={sizes}
          />
        </div>
        <Motion
          animate={isHovered ? 'hover' : 'initial'}
          transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.2 }}
          variants={{
            initial: {
              height: 0,
              maxHeight: 0,
            },
            hover: {
              height: 'auto',
              maxHeight: '1000px',
              marginBottom: '1rem',
            },
          }}
        >
          <Image
            alt=""
            className="rounded-xl object-cover"
            src={appUiImageSrc}
            sizes={sizes}
            width={700}
            height={600}
          />
        </Motion>
      </div>
    </li>
  );
};

const MobileCard = ({ mainImageSrc, appUiImageSrc, tagKey }: CardProps) => {
  const t = useTranslations();
  return (
    <div className="relative h-[40rem] md:h-[55rem]">
      <div className="relative h-[35rem] w-screen md:h-[45rem]">
        <div className="absolute z-[99] ml-6 mt-4 inline-flex px-3 py-2">
          <BackdropBlur variant="pill" />
          <TypographyV3
            Element="span"
            color="light"
            variant="eyebrow-large"
            className="relative"
          >
            {t(tagKey)}
          </TypographyV3>
        </div>
        <Image
          className="rounded-xl object-cover"
          alt=""
          src={mainImageSrc}
          fill
          sizes="50vw"
        />
      </div>
      <Motion
        className="absolute bottom-0 flex w-full justify-center"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, type: 'tween', delay: 0.5 }}
      >
        <Image
          alt=""
          className="rounded-xl object-cover"
          src={appUiImageSrc}
          width={300}
          height={265}
          sizes="30vw"
        />
      </Motion>
    </div>
  );
};

const MobileCards = () => {
  const t = useTranslations();
  return (
    <SwipeableProvider className="block lg:hidden">
      <div className="flex flex-col-reverse">
        <LayoutGrid>
          <GridItem colEnd={{ sm: 'full' }} colStart={{ sm: 'main' }}>
            <div className="relative mr-6 flex items-center justify-between">
              <SwipeableProgressRatio color="dark" className="flex-1" />
              <NavButtons />
            </div>
          </GridItem>
        </LayoutGrid>
        <SwipeableSlider className="pr-6">
          <MobileCard
            tagKey="relaxed_card_tag"
            mainImageSrc="/blue-sky/why-oura/relaxed.jpeg?flip=h"
            appUiImageSrc={t('relaxed_app_ui_src')}
          />
          <MobileCard
            tagKey="restored_card_tag"
            mainImageSrc="/blue-sky/why-oura/restored.jpeg"
            appUiImageSrc={t('restored_app_ui_src')}
          />
          <MobileCard
            tagKey="engaged_card_tag"
            mainImageSrc="/blue-sky/why-oura/engaged.png"
            appUiImageSrc={t('engaged_app_ui_src')}
          />
        </SwipeableSlider>
      </div>
    </SwipeableProvider>
  );
};

const DesktopCards = () => {
  const t = useTranslations();
  return (
    <ul className="mt-5 hidden h-[50rem] gap-x-4 lg:flex">
      <DesktopCard
        tagKey="relaxed_card_tag"
        mainImageSrc="/blue-sky/why-oura/relaxed.jpeg?flip=h"
        appUiImageSrc={t('relaxed_app_ui_src')}
      />
      <DesktopCard
        tagKey="restored_card_tag"
        mainImageSrc="/blue-sky/why-oura/restored.jpeg"
        appUiImageSrc={t('restored_app_ui_src')}
      />
      <DesktopCard
        tagKey="engaged_card_tag"
        mainImageSrc="/blue-sky/why-oura/engaged.png"
        appUiImageSrc={t('engaged_app_ui_src')}
      />
    </ul>
  );
};

const ExpandableCards = (): JSX.Element => {
  return (
    <InverseHeader enter>
      <Motion
        initial={{ filter: 'blur(5px)', opacity: 0 }}
        whileInView={{ filter: 'blur(0px)', opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.5 }}
        data-cy="why-oura-wellness-cards"
        aria-hidden // hidden from screen readers because it is all visual animations of images
      >
        <MobileCards />
        <DesktopCards />
      </Motion>
    </InverseHeader>
  );
};

export default ExpandableCards;
